import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { TranslationService } from '@alfresco/adf-core';

import { SnackbarErrorAction, SnackbarInfoAction } from '../../../store';
import { AiUMCService } from '../services';
import { ApiSequencingService } from '../../../services/api-sequencing.service';
import {
  loadAIUMCReleaseNotes,
  loadAIUMCReleaseNotesCompleted,
  loadAIUMCReleaseNotesFailed,
  loadAIUMCReleaseNotesStart,
} from './actions';
import { INote } from '../model';

@Injectable()
export class AiUMCEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: AiUMCService,
    private readonly store: Store,
    private readonly translationService: TranslationService,
    private readonly apiSequencingService: ApiSequencingService
  ) {}

  public loadAIUMCReleaseNotes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAIUMCReleaseNotes),
      tap(() => {
        this.store.dispatch(loadAIUMCReleaseNotesStart());
      }),
      // Wait for citation API to complete before proceeding
      switchMap(
        (action: {
          type: Required<string>;
          nodeId: Required<string>;
          sectionId: Required<string>;
        }) =>
          this.apiSequencingService.waitForCitationApiCompletion().pipe(
            switchMap(() =>
              this.service
                .getGeneratedAIUMCData({
                  nodeId: action.nodeId,
                  sectionId: action.sectionId,
                })
                .pipe(
                  map((notes: INote[]) => {
                    this.store.dispatch(
                      new SnackbarInfoAction(
                        this.translationService.instant(
                          notes.length < 1
                            ? 'UPDATE_MANAGER_COMMENTS.SUCCESS_WITHOUT_RECORDS'
                            : 'UPDATE_MANAGER_COMMENTS.SUCCESS'
                        )
                      )
                    );
                    return loadAIUMCReleaseNotesCompleted({ notes });
                  }),
                  catchError((err) => {
                    const message = this.translationService.instant(
                      'UPDATE_MANAGER_COMMENTS.ERROR'
                    );
                    console.error(
                      'Error loading AI UMC Release Notes',
                      +err?.response?.body?.reason
                    );
                    this.store.dispatch(new SnackbarErrorAction(message));
                    return of(loadAIUMCReleaseNotesFailed());
                  })
                )
            )
          )
      )
    )
  );
}
